import { uid } from '../../../../lib/uid';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';

/*
  Plausible awards and awarding bodies, which is the right call here for the reason
  `sampleSpecialSkill`'s skills and `sampleTraining`'s course titles are: a recognition never
  leaves `rsys_` and never lands in the identity store, so an obviously synthetic one would make
  the generated profile worse at the one job it has. The rule is about where a value LANDS, not
  which generator it came from — `samplePersonnel` and `sampleFamilyMember` write into `rsys_pii`
  and are the two that must not.

  Captured from the hospital system's own `sampleRecognition`, whose pools these are.
*/
const TITLES = [
  'Employee of the Year',
  'Outstanding Service Award',
  'Most Dedicated Staff',
  'Patient Safety Champion',
  'Service Excellence Award',
  'Perfect Attendance Award',
  'Leadership Excellence Award',
  'Community Service Award',
];

const AWARDING_BODIES = [
  'Hospital Administration',
  'Department of Health',
  'Philippine Nurses Association',
  'Medical Staff Council',
  'Human Resources Department',
  'Regional Health Office',
  'Philippine Hospital Association',
];

const CITATIONS = [
  'Recognized for exemplary performance and dedication to service.',
  'Awarded for consistent excellence in service delivery.',
  'Honored for outstanding contribution to the department.',
  'Recognized for going above and beyond in service to the public.',
  'Awarded for leadership and commitment to quality.',
];

export interface SampleRecognition {
  title: string;
  awardedBy: string;

  /** `YYYY-MM-DD`, and never later than today — the server refuses a future award. */
  dateAwarded: string;

  description: string;
}

function pick<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)]!;
}

/**
 * Test data for the Recognition form's Autogenerate.
 *
 * **The date is always in the PAST**, because the server refuses a recognition awarded in the
 * future — a generator that produced one would fill a form the reader then has to correct before
 * it saves, which is the one thing Autogenerate exists to prevent. Built by walking back from
 * today rather than by picking a year at random, so it can never drift past the guard as the
 * calendar moves.
 *
 * **The title is suffixed**, like every other generator here. Autogenerate is most useful clicked
 * repeatedly, and a suffix keeps a run of clicks legible in the panel — this table carries no
 * unique index, so an unsuffixed second click would save quietly and leave two rows a reader
 * cannot tell apart.
 *
 * **It fills the optional fields too.** The floor is that the primary action enables; the point is
 * that the screens behind the form get exercised, and a record carrying only its required field
 * produces a panel of em dashes that exercises nothing.
 */
export function sampleRecognition(): SampleRecognition {
  const tag = uid().slice(0, 6).toUpperCase();

  /*
    Between roughly one and ten years back. `todayCalendarDate()` is the product's own
    conversion-free today — a `Date` built here and formatted would put a calendar date through
    the instant path, which is the rule `formatDateOnly` exists for.
  */
  const [year, month, day] = todayCalendarDate().split('-').map(Number);
  const yearsBack = 1 + Math.floor(Math.random() * 9);
  const pad = (n: number) => String(n).padStart(2, '0');

  // The 28th is the highest day every month carries, so this can never name a date that does not
  // exist — 31 February would be normalised forward by anything that later parses it.
  const safeDay = Math.min(day!, 28);

  return {
    title: `${pick(TITLES)} ${tag}`,
    awardedBy: pick(AWARDING_BODIES),
    dateAwarded: `${year! - yearsBack}-${pad(month!)}-${pad(safeDay)}`,
    description: `${pick(CITATIONS)} Generated sample ${tag}.`,
  };
}
